import { Box, Card, Typography } from "@mui/material";
import { useMemo } from "react";
import { mealTypes } from "../../../../../util/dropList";
import { canWriteModule } from "../../../../../util/roleModuleAccess";

const PharmacyPrescriptionCard = ({ item, onDispense, readOnly = false }) => {
  const canManagePharmacy = canWriteModule("pharmacy");
  const mandateDataRaw = sessionStorage.getItem("mandateData");
  const clinicStocks = useMemo(() => {
    if (!mandateDataRaw) return [];
    try {
      const reqData = JSON.parse(mandateDataRaw);
      return Array.isArray(reqData?.stocks) ? reqData.stocks : [];
    } catch {
      return [];
    }
  }, [mandateDataRaw]);

  const isControlledSubstance = useMemo(() => {
    if (!item?.stockId) return false;
    const reqStock = clinicStocks?.find(
      (stc) => stc?.stockId === item?.stockId,
    );
    if (!reqStock) return false;
    const isScheduleH1 =
      (reqStock?.drugSchedule ?? "")?.toLowerCase() === "schedule h1";
    const isNdps =
      reqStock?.ndps === true ||
      reqStock?.ndps === "Y" ||
      reqStock?.ndps === "y";
    return isScheduleH1 || isNdps;
  }, [item?.stockId, clinicStocks]);

  const frequencyLabel = Array.isArray(item?.frequency)
    ? item.frequency
        .map((freq) => freq?.name ?? freq?.value ?? freq)
        .filter(Boolean)
        .join(", ")
    : item?.frequency;

  const routeLabel =
    typeof item?.route === "object"
      ? (item?.route?.name ?? item?.route?.value)
      : item?.route;

  const mealLabel =
    mealTypes?.find((ml) => ml?.value === item?.meal)?.name || item?.meal;

  const isPending =
    typeof item?.itemIssued !== "boolean" &&
    !readOnly &&
    item?.isEdit !== false;
  const isDispensed = item?.itemIssued === true;
  const isRejected = item?.itemIssued === false;

  const detailTags = [
    item?.dosage ? { label: "Dose", value: item.dosage } : null,
    routeLabel ? { label: "Route", value: routeLabel } : null,
    frequencyLabel ? { label: "Freq", value: frequencyLabel } : null,
    item?.duration
      ? { label: "Duration", value: `${item.duration} days` }
      : null,
    mealLabel ? { label: "Meal", value: mealLabel } : null,
    item?.remark ? { label: "Remark", value: item.remark } : null,
  ].filter(Boolean);

  return (
    <Card
      sx={{
        borderRadius: "8px",
        boxShadow: "0px 1px 4px rgba(0, 0, 0, 0.06)",
        border: "1px solid #e0e0e0",
        p: 2,
        mb: 1.5,
        backgroundColor: "#ffffff",
      }}
    >
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={1.5}
      >
        <Typography
          sx={{
            fontSize: "15px",
            fontWeight: 700,
            color: "#212121",
          }}
        >
          {item?.plName}
        </Typography>

        <Box display="flex" alignItems="center" gap={1.5} flexWrap="wrap">
          {isControlledSubstance ? (
            <Box
              sx={{
                backgroundColor: "#ffebee",
                color: "#d32f2f",
                fontSize: "12px",
                fontWeight: 600,
                px: 1.2,
                py: 0.3,
                borderRadius: "4px",
                border: "1px solid #ffcdd2",
              }}
            >
              Controlled substance
            </Box>
          ) : null}

          {isDispensed || isRejected ? (
            <Typography
              className={`${
                isDispensed ? "green-back5" : "red-back2"
              } white-color status-con fs12 font-medium con-fit`}
            >
              {isDispensed ? "Dispensed" : "Rejected"}
            </Typography>
          ) : null}

          {canManagePharmacy && isPending ? (
            <Typography
              className="blu-back white-color fs14 font-medium con-fit cursor pv5 ph15 br5"
              onClick={() => onDispense?.(item)}
            >
              Dispense
            </Typography>
          ) : null}
        </Box>
      </Box>

      <Box display="flex" alignItems="center" flexWrap="wrap" gap={1}>
        {detailTags.map((tag) => (
          <Box
            key={`${item?.detailId ?? item?.i}-${tag.label}`}
            sx={{
              backgroundColor: "#f5f5f5",
              color: "#616161",
              fontSize: "11px",
              fontWeight: 500,
              px: 1.2,
              py: 0.4,
              borderRadius: "4px",
              border: "1px solid #e0e0e0",
            }}
          >
            {tag.label}: {tag.value}
          </Box>
        ))}
      </Box>
    </Card>
  );
};

export default PharmacyPrescriptionCard;
